import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import AuthService from "../services/auth.service";
import DataService from "../services/data.service";

const pad = (value) => String(value).padStart(2, "0");

const toDateTimeLocalValue = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;

const toReportDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date;
};

const formatFileDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value ?? "").replace("T", " ");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours()
  )}${pad(date.getMinutes())}`;
};

const cleanFilePart = (value) =>
  String(value ?? "")
    .trim()
    .replace(/[<>:"/\\|?*]/g, "-")
    .replace(/\s+/g, " ");

const cleanSheetName = (value, fallback) => {
  const name = cleanFilePart(value || fallback)
    .replace(/[[\]]/g, "-")
    .slice(0, 31)
    .trim();
  return name || fallback;
};

const downloadExcelSheet = (report) => {
  const rowsByMerde = report.rows.reduce((groups, row) => {
    const key = row.merde || row.IMEI || "Merde";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
    return groups;
  }, new Map());

  const workbook = XLSX.utils.book_new();

  [...rowsByMerde.entries()].forEach(([merde, rows], index) => {
    const sheetRows = rows.map((row) => ({
      Tid: toReportDate(row.time),
      "Lampe 1": row.lamps[0] ?? null,
      "Lampe 2": row.lamps[1] ?? null,
      "Lampe 3": row.lamps[2] ?? null,
      "Lampe 4": row.lamps[3] ?? null,
      "Lampe 5": row.lamps[4] ?? null,
      "Lampe 6": row.lamps[5] ?? null,
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetRows, {
      header: ["Tid", "Lampe 1", "Lampe 2", "Lampe 3", "Lampe 4", "Lampe 5", "Lampe 6"],
      cellDates: true,
    });

    worksheet["!cols"] = [
      { wch: 18 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
    ];

    Object.keys(worksheet).forEach((cellRef) => {
      if (cellRef.startsWith("!") || cellRef.match(/[A-Z]+1$/)) return;
      if (cellRef[0] === "A") {
        worksheet[cellRef].z = "yyyy-mm-dd hh:mm";
        return;
      }
      if (typeof worksheet[cellRef].v === "number") {
        worksheet[cellRef].z = "0.0%";
        worksheet[cellRef].v = worksheet[cellRef].v / 100;
      }
    });

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      cleanSheetName(merde, `Merde ${index + 1}`)
    );
  });

  const clusterName = cleanFilePart(report.cluster?.nick ?? "Lokasjon");
  const from = formatFileDateTime(report.fromDate);
  const to = formatFileDateTime(report.toDate);
  XLSX.writeFile(workbook, `${clusterName} (${from} - ${to}).xlsx`);
};

export default function ModeratorHourlyReport() {
  const [clusters, setClusters] = useState([]);
  const [clusterId, setClusterId] = useState("");
  const [selectedSystems, setSelectedSystems] = useState([]);
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    date.setMinutes(0, 0, 0);
    return toDateTimeLocalValue(date);
  });
  const [toDate, setToDate] = useState(() => {
    const date = new Date();
    date.setMinutes(0, 0, 0);
    return toDateTimeLocalValue(date);
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const currentUser = AuthService.getCurrentUser();
    if (!currentUser) return;

    DataService.getClusters(currentUser.id).then(
      (response) => {
        const nextClusters = response.data ?? [];
        setClusters(nextClusters);
        if (nextClusters.length > 0) {
          const firstCluster = nextClusters[0];
          setClusterId(String(firstCluster.id));
          setSelectedSystems((firstCluster.systems ?? []).map((system) => system.id));
        }
      },
      () => {
        setMessage("Kunne ikke hente lokasjoner/systemer.");
      }
    );
  }, []);

  const selectedCluster = useMemo(
    () => clusters.find((cluster) => String(cluster.id) === String(clusterId)),
    [clusters, clusterId]
  );

  const systems = selectedCluster?.systems ?? [];

  const toggleSystem = (systemId) => {
    setSelectedSystems((prev) =>
      prev.includes(systemId)
        ? prev.filter((id) => id !== systemId)
        : [...prev, systemId]
    );
  };

  const handleClusterChange = (event) => {
    const nextClusterId = event.target.value;
    const nextCluster = clusters.find((cluster) => String(cluster.id) === nextClusterId);
    setClusterId(nextClusterId);
    setSelectedSystems((nextCluster?.systems ?? []).map((system) => system.id));
  };

  const createReport = async () => {
    setMessage("");

    if (!clusterId || selectedSystems.length === 0) {
      setMessage("Velg lokasjon og minst ett system.");
      return;
    }

    setLoading(true);
    try {
      const response = await DataService.getHourlyLampReport({
        clusterId: Number(clusterId),
        systemIds: selectedSystems,
        fromDate,
        toDate,
      });

      if (!response.data?.rows?.length) {
        setMessage("Ingen data funnet for valgt periode.");
        return;
      }

      downloadExcelSheet(response.data);
      setMessage(`Rapport laget med ${response.data.rows.length} rader.`);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Kunne ikke lage rapport.";
      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <span className="viewcard41">
      <div className="moderator-report">
        <h2>Driftsrapport</h2>

        <div className="report-controls">
          <label>
            Lokasjon
            <select value={clusterId} onChange={handleClusterChange}>
              {clusters.map((cluster) => (
                <option key={cluster.id} value={cluster.id}>
                  {cluster.nick} - {cluster.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Fra
            <input
              type="datetime-local"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </label>

          <label>
            Til
            <input
              type="datetime-local"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </label>
        </div>

        <div className="report-system-list">
          {systems.map((system) => (
            <label key={system.id} className="report-system-option">
              <input
                type="checkbox"
                checked={selectedSystems.includes(system.id)}
                onChange={() => toggleSystem(system.id)}
              />
              <span>
                {system.nick} <span className="imei">({system.IMEI})</span>
              </span>
            </label>
          ))}
        </div>

        <button className="report-action" onClick={createReport} disabled={loading}>
          {loading ? "Lager rapport..." : "Last ned driftsrapport"}
        </button>

        {message ? <div className="report-message">{message}</div> : null}
      </div>
    </span>
  );
}
